import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { displayLocation } from '../../utils/locationUtils';
import { translatePA } from '../../utils/practiceAreaUtils';
import { savedAttorneyAPI, analyticsAPI } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { CASE_CATEGORIES } from '../../constants/caseCategories';
import StarRating from './StarRating';

const SERVER_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');

const getParentCategories = (practiceAreas) => {
  const parents = new Set();
  for (const area of practiceAreas) {
    const parent = CASE_CATEGORIES.find(cat => cat.subs.includes(area) || cat.label === area);
    if (parent) parents.add(parent.label);
  }
  return [...parents];
};

const useWindowWidth = () => {
  const [w, setW] = useState(window.innerWidth);
  React.useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return w;
};

const RatingInfoIcon = ({ onClick }) => (
  <button onClick={e => { e.preventDefault(); e.stopPropagation(); onClick(); }} style={{
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: '15px', height: '15px', borderRadius: '50%',
    backgroundColor: '#3182ce', color: 'white',
    border: 'none', cursor: 'pointer', fontSize: '10px', fontWeight: '700',
    fontStyle: 'italic', flexShrink: 0, lineHeight: 1,
    verticalAlign: 'middle', marginLeft: '4px',
  }}>i</button>
);

const RatingInfoPopup = ({ onClose }) => {
  const { t } = useLanguage();
  return (
  <div style={{
    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
  }} onClick={onClose}>
    <div style={{
      backgroundColor: 'white', borderRadius: '14px', padding: '1.75rem',
      width: '460px', maxWidth: '92vw', boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
    }} onClick={e => e.stopPropagation()}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#1a365d', fontWeight: '700' }}>{t('findAttorney.ratingInfoTitle')}</h3>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: '#718096', lineHeight: 1 }}>×</button>
      </div>
      <p style={{ color: '#4a5568', fontSize: '0.92rem', lineHeight: '1.8', margin: '0 0 1.25rem' }}>
        {t('findAttorney.ratingInfoBody')}
      </p>
      <div style={{ backgroundColor: '#fff5f5', borderLeft: '3px solid #fc8181', borderRadius: '0 6px 6px 0', padding: '0.9rem 1rem' }}>
        <p style={{ color: '#742a2a', fontSize: '0.85rem', fontWeight: '700', margin: '0 0 0.4rem' }}>{t('findAttorney.ratingDisclaimer')}</p>
        <p style={{ color: '#742a2a', fontSize: '0.85rem', lineHeight: '1.65', margin: 0 }}>
          {t('findAttorney.ratingDisclaimerText')}
        </p>
      </div>
    </div>
  </div>
  );
};

/** Save/bookmark toggle — logged-out visitors are sent to /login. */
export const SaveToggle = ({ attorneyId, initialSaved, compact }) => {
  const { isClient } = useAuth();
  const navigate = useNavigate();
  const [saved, setSaved] = useState(!!initialSaved);
  const [busy, setBusy] = useState(false);

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isClient) { navigate('/login'); return; }
    if (busy) return;
    setBusy(true);
    try {
      if (saved) {
        await savedAttorneyAPI.remove(attorneyId);
        setSaved(false);
      } else {
        await savedAttorneyAPI.add(attorneyId);
        setSaved(true);
      }
    } catch (_) {
      // ignore — leave state unchanged on failure
    } finally {
      setBusy(false);
    }
  };

  return (
    <button onClick={handleClick} style={{
      display: 'flex', alignItems: 'center', gap: '0.25rem',
      background: 'none', border: 'none', cursor: 'pointer', padding: 0,
      color: saved ? '#2b6cb0' : '#718096', fontSize: compact ? '0.75rem' : '0.8rem', fontWeight: '600',
    }}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill={saved ? '#2b6cb0' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
      </svg>
      {saved ? 'Сақланган' : 'Сақлаш'}
    </button>
  );
};

/** Overlaps the bottom edge of an attorney's photo — Pro gets a crown, VIP the same pill with a
 *  diamond instead, both grandfathered onto every attorney who was already subscribed pre-tiers. */
export const TierBadge = ({ tier, size = 90 }) => {
  if (tier !== 'pro' && tier !== 'vip') return null;
  const isVip = tier === 'vip';
  const small = size < 80;
  return (
    <div style={{
      position: 'absolute', bottom: 0, left: '50%', transform: 'translate(-50%, 45%)',
      display: 'flex', alignItems: 'center', gap: '0.22rem',
      backgroundColor: 'white', borderRadius: '999px',
      padding: small ? '0.15rem 0.5rem' : '0.22rem 0.65rem',
      boxShadow: '0 2px 8px rgba(0,0,0,0.2)', border: '1px solid #f1f1f1',
      fontSize: small ? '0.6rem' : '0.7rem', fontWeight: '800', letterSpacing: '0.03em',
      color: isVip ? '#7c3aed' : '#c05621', whiteSpace: 'nowrap', zIndex: 2,
    }}>
      <span style={{ fontSize: small ? '0.72rem' : '0.85rem', lineHeight: 1 }}>{isVip ? '💎' : '👑'}</span>
      {isVip ? 'VIP' : 'PRO'}
    </div>
  );
};

/** Reusable attorney card — used on the search-results list and the saved-attorneys list. */
const AttorneyCard = ({ attorney, rating, saved }) => {
  const [showRatingInfo, setShowRatingInfo] = useState(false);
  const { language, t } = useLanguage();
  const isMobile = useWindowWidth() < 768;

  const avatarEl = (size) => (
    <div style={{ position: 'relative', width: size, flexShrink: 0 }}>
      <div style={{ ...styles.avatar, width: size, height: Math.round(size * 1.22) + 'px' }}>
        {attorney.profile_picture ? (
          <img src={`${SERVER_URL}${attorney.profile_picture}`} alt={attorney.full_name}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
          </svg>
        )}
      </div>
      <TierBadge tier={attorney.tier} size={size} />
    </div>
  );

  const locationEl = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', color: '#718096', fontSize: '0.85rem' }}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/>
      </svg>
      {attorney.location ? displayLocation(attorney.location, t, language) : t('findAttorney.noLocationSpecified')}
    </div>
  );

  const starsRow = (
    <StarRating
      average={rating?.average ?? 0}
      count={rating?.count ?? 0}
      size={14}
      InfoIcon={<RatingInfoIcon onClick={() => setShowRatingInfo(true)} />}
    />
  );

  const saveRow = (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <SaveToggle attorneyId={attorney.id} initialSaved={saved} compact />
    </div>
  );

  const callBtn = attorney.phone && (
    <a href={`tel:${attorney.phone}`} onClick={() => analyticsAPI.callClick(attorney.id)}
      style={{ ...styles.callBtn, textDecoration: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}
      onMouseOver={e => e.currentTarget.style.backgroundColor = '#2b6cb0'}
      onMouseOut={e => e.currentTarget.style.backgroundColor = '#3182ce'}
    >
      <span>{t('findAttorney.callBtn')}</span>
      <span style={{ fontSize: '0.78rem', fontWeight: '500', opacity: 0.9 }}>{attorney.phone}</span>
    </a>
  );

  const secondaryButtons = (
    <div style={{ display: 'flex', gap: '0.5rem' }}>
      <Link to={`/attorney/${attorney.id}`} style={{ ...styles.secondaryBtn, flex: 1 }}>{t('findAttorney.profileBtn')}</Link>
      <Link to={`/contact/${attorney.id}`} style={{ ...styles.secondaryBtn, flex: 1 }}>{t('findAttorney.messageBtn')}</Link>
    </div>
  );

  const bioEl = attorney.bio && (
    <div style={styles.infoRow}>
      <svg style={styles.infoIcon} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
      </svg>
      <p style={styles.bio}>{attorney.bio}</p>
    </div>
  );

  if (isMobile) {
    const parents = getParentCategories(attorney.practice_areas || []);
    return (
      <div style={{ ...styles.card, flexDirection: 'column', gap: '0.75rem', alignItems: 'stretch' }}>
        {showRatingInfo && <RatingInfoPopup onClose={() => setShowRatingInfo(false)} />}
        {/* Top row: avatar + name/location */}
        <div style={{ display: 'flex', gap: '0.85rem', alignItems: 'flex-start' }}>
          <Link to={`/attorney/${attorney.id}`} style={{ textDecoration: 'none' }}>{avatarEl(72)}</Link>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Link to={`/attorney/${attorney.id}`} style={styles.nameLink}>{attorney.full_name}</Link>
            <div style={{ marginBottom: '0.25rem' }}>{starsRow}</div>
            {locationEl}
            {attorney.experience_label && (
              <span style={{ fontSize: '0.78rem', color: '#2b6cb0', backgroundColor: '#ebf8ff', padding: '0.1rem 0.55rem', borderRadius: '20px', display: 'inline-block', marginTop: '0.3rem' }}>
                {attorney.experience_label}
              </span>
            )}
          </div>
        </div>
        {/* Practice areas - pills with icon */}
        {parents.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
            <svg style={{ flexShrink: 0, marginTop: '3px', color: '#718096' }} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
            </svg>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
              {parents.map(p => <span key={p} style={styles.tag}>{translatePA(p, language)}</span>)}
            </div>
          </div>
        )}
        {bioEl}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          {saveRow}
        </div>
        {callBtn}
        {secondaryButtons}
      </div>
    );
  }

  // Desktop layout
  return (
  <div
    style={styles.card}
    onMouseOver={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)'}
    onMouseOut={e => e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)'}
  >
    <Link to={`/attorney/${attorney.id}`} style={{ textDecoration: 'none' }}>{avatarEl(90)}</Link>

    <div style={styles.cardBody}>
      <Link to={`/attorney/${attorney.id}`} style={styles.nameLink}>{attorney.full_name}</Link>
      <div style={{ marginBottom: '0.5rem' }}>{starsRow}</div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
        <div style={{ ...styles.location, marginBottom: 0 }}>{locationEl}</div>
        {attorney.experience_label && (
          <span style={{ fontSize: '0.82rem', color: '#2b6cb0', backgroundColor: '#ebf8ff', padding: '0.15rem 0.6rem', borderRadius: '20px', whiteSpace: 'nowrap' }}>
            {attorney.experience_label}
          </span>
        )}
      </div>

      {attorney.practice_areas?.length > 0 && (
        <div style={styles.infoRow}>
          <svg style={styles.infoIcon} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
          </svg>
          <div style={styles.practiceAreas}>
            {getParentCategories(attorney.practice_areas).slice(0, 4).map(parent => (
              <span key={parent} style={styles.tag}>{translatePA(parent, language)}</span>
            ))}
            {getParentCategories(attorney.practice_areas).length > 4 && (
              <span style={styles.tag}>+{getParentCategories(attorney.practice_areas).length - 4}</span>
            )}
          </div>
        </div>
      )}

      {bioEl}
    </div>

    <div style={{ ...styles.cardAction, flexDirection: 'column', alignItems: 'stretch', gap: '0.6rem' }}>
      {showRatingInfo && <RatingInfoPopup onClose={() => setShowRatingInfo(false)} />}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {saveRow}
      </div>
      {callBtn}
      {secondaryButtons}
    </div>
  </div>
  );
};

const styles = {
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '1.5rem',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    transition: 'box-shadow 0.2s',
    display: 'flex',
    gap: '1.25rem',
    alignItems: 'flex-start',
  },
  avatar: {
    width: '90px',
    height: '110px',
    borderRadius: '8px',
    backgroundColor: '#e8edf2',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#a0aec0',
    flexShrink: 0,
  },
  cardBody: {
    flex: 1,
    minWidth: 0,
  },
  nameLink: {
    fontSize: '1.15rem',
    fontWeight: '700',
    color: '#2b6cb0',
    textDecoration: 'none',
    display: 'inline-block',
    marginBottom: '0.25rem',
  },
  location: {
    color: '#718096',
    fontSize: '0.88rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.3rem',
    marginBottom: '0.75rem',
  },
  infoRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.5rem',
    marginBottom: '0.6rem',
  },
  infoIcon: {
    flexShrink: 0,
    marginTop: '1px',
    color: '#718096',
  },
  practiceAreas: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.4rem',
  },
  tag: {
    backgroundColor: '#ebf8ff',
    color: '#2b6cb0',
    padding: '0.2rem 0.65rem',
    borderRadius: '12px',
    fontSize: '0.78rem',
  },
  bio: {
    color: '#4a5568',
    fontSize: '0.88rem',
    lineHeight: '1.55',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  cardAction: {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'flex-end',
    gap: '0.6rem',
    minWidth: '160px',
  },
  callBtn: {
    backgroundColor: '#3182ce',
    color: 'white',
    border: 'none',
    padding: '0.65rem 1.1rem',
    borderRadius: '8px',
    fontSize: '0.85rem',
    fontWeight: '700',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  secondaryBtn: {
    backgroundColor: 'white',
    color: '#2d3748',
    border: '1px solid #e2e8f0',
    padding: '0.55rem 0.9rem',
    borderRadius: '8px',
    fontSize: '0.82rem',
    fontWeight: '600',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    textDecoration: 'none',
    textAlign: 'center',
  },
};

export default AttorneyCard;
